/**
 * @fileoverview Campaign state management hook
 * @description Управление состоянием кампаний: CRUD операции, получатели
 *
 * Data fetching powered by TanStack Query (useCampaignsQuery / useCampaignDetailQuery).
 * Local state retained for form/UI/mutation concerns.
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import { useCampaignsQuery, useCampaignDetailQuery } from '../hooks/outreach/queries';
import {
    updateCampaign as apiUpdateCampaign,
    duplicateCampaign as apiDuplicateCampaign
} from '../api/outreach/campaigns';
import {
    addRecipients as apiAddRecipients,
    removeRecipient as apiRemoveRecipient,
    attachContactList as apiAttachContactList,
    detachContactList as apiDetachContactList
} from '../api/outreach/recipients';
import type {
    Campaign,
    CampaignFormData,
    Recipient,
    CampaignDetails
} from '../types/outreach';

/** Опции для инициализации хука */
interface UseCampaignStateOptions {
    onTemplatesFetch?: () => Promise<unknown>;
    onContactListsFetch?: () => Promise<unknown>;
}

const createEmptyCampaignForm = (): CampaignFormData => ({
    name: '',
    description: '',
    subjectTemplate: '',
    bodyTemplate: '',
    fromAccountId: '',
    templateId: '',
    // Phase 97.8 W2: instantlyCampaignId / instantlyEmailAccount removed from form (Instantly cutover).
    contactListIds: [],
    minDelay: 60,
    maxDelay: 120,
    dailyLimit: 0
});

/**
 * Campaign state management hook.
 * TanStack Query for data fetching; local state for form/UI.
 */
export const useCampaignState = ({
    onTemplatesFetch,
    onContactListsFetch
}: UseCampaignStateOptions = {}) => {
    const qc = useQueryClient();

    // =========================================================================
    // FILTERS (local state — drive TQ query key)
    // =========================================================================
    const [ownerFilter, setOwnerFilter] = useState<'my' | 'team' | 'all'>('all');
    const [teamFilter, setTeamFilter] = useState<string | null>(null);
    const [managerFilter, setManagerFilter] = useState<string | null>(null);

    const filters = useMemo(
        () => ({ owner: ownerFilter, teamId: teamFilter, managerId: managerFilter }),
        [ownerFilter, teamFilter, managerFilter]
    );

    // =========================================================================
    // TQ QUERIES
    // =========================================================================
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const campaignsQuery = useCampaignsQuery(filters);
    const detailQuery = useCampaignDetailQuery(selectedId);

    // Derived from TQ
    // Cast to OutreachCampaign[] — JS query returns untyped data; canonical shape is OutreachCampaign.
    const campaigns: Campaign[] = ((campaignsQuery.data as any)?.items || []) as Campaign[];
    const loading = campaignsQuery.isLoading;
    const userRole: string = (campaignsQuery.data as any)?.userRole || 'MANAGER';
    const teams: { id: string; name: string }[] = (campaignsQuery.data as any)?.teams || [];
    const managers: { id: string; name: string; teamId?: string }[] = (campaignsQuery.data as any)?.managers || [];
    const details: CampaignDetails | null = (detailQuery.data as CampaignDetails | undefined) ?? null;
    const detailsLoading = detailQuery.isLoading;

    // =========================================================================
    // LOCAL UI / FORM STATE
    // =========================================================================
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [campaignForm, setCampaignForm] = useState<CampaignFormData>(createEmptyCampaignForm);
    const [campaignSaving, setCampaignSaving] = useState(false);
    const [recipientsInput, setRecipientsInput] = useState('');
    const [addingRecipients, setAddingRecipients] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [testStatus, setTestStatus] = useState<string | null>(null);
    const [attachListId, setAttachListId] = useState('');
    const [attachLoading, setAttachLoading] = useState(false);
    const [actionInProgress, setActionInProgress] = useState<string | null>(null);
    const [isCreateWizardOpen, setIsCreateWizardOpen] = useState(false);

    // =========================================================================
    // INVALIDATION HELPERS
    // =========================================================================
    const invalidateCampaigns = useCallback(
        () => qc.invalidateQueries({ queryKey: queryKeys.outreach.campaigns.all }),
        [qc]
    );

    const invalidateDetail = useCallback(
        (id?: string | null) => qc.invalidateQueries({
            queryKey: queryKeys.outreach.campaigns.detail(id || selectedId)
        }),
        [qc, selectedId]
    );

    // =========================================================================
    // FORM SYNC from TQ detail data
    // =========================================================================
    useEffect(() => {
        // Cast to canonical CampaignDetails — JS query hook returns untyped data.
        const data = detailQuery.data as CampaignDetails | undefined;
        if (!data?.campaign) return;
        setCampaignForm({
            name: data.campaign.name || '',
            description: data.campaign.description || '',
            subjectTemplate: data.campaign.subjectTemplate || '',
            bodyTemplate: data.campaign.bodyTemplate || '',
            fromAccountId: data.campaign.fromAccount?.id || '',
            templateId: data.campaign.templateId || '',
            // Phase 97.8 W2: instantlyCampaignId / instantlyEmailAccount no longer round-tripped.
            contactListIds: Array.isArray(data.campaign.contactLists)
                ? data.campaign.contactLists.map((item: any) => item?.listId || item?.id).filter(Boolean) as string[]
                : [],
            minDelay: data.campaign.settings?.minDelay ?? 60,
            maxDelay: data.campaign.settings?.maxDelay ?? 120,
            dailyLimit: data.campaign.settings?.dailyLimit ?? 0
        });
        setRecipientsInput('');
        setNotice(null);
        setTestEmail('');
        setTestStatus(null);
        setAttachListId('');
    }, [detailQuery.data]);

    // =========================================================================
    // FACADE: fetchCampaigns / fetchDetails (OutreachContext compat)
    // =========================================================================
    const fetchCampaigns = useCallback(async (
        preserveSelection = false,
        skipSelectionUpdate = false
    ): Promise<Campaign[]> => {
        await invalidateCampaigns();
        const cached = qc.getQueryData<{ items?: Campaign[] }>(
            queryKeys.outreach.campaigns.list(filters)
        );
        const items = cached?.items || [];
        if (!skipSelectionUpdate && !preserveSelection) {
            setSelectedId(null);
        }
        return items;
    }, [invalidateCampaigns, qc, filters]);

    const fetchDetails = useCallback(async (
        campaignId: string
    ): Promise<CampaignDetails | null> => {
        if (!campaignId) return null;
        await qc.invalidateQueries({
            queryKey: queryKeys.outreach.campaigns.detail(campaignId)
        });
        return qc.getQueryData<CampaignDetails>(
            queryKeys.outreach.campaigns.detail(campaignId)
        ) || null;
    }, [qc]);

    // =========================================================================
    // WIZARD HANDLERS
    // =========================================================================
    const startCreateCampaign = useCallback(() => {
        setIsCreateWizardOpen(true);
        onTemplatesFetch?.();
        onContactListsFetch?.();
    }, [onTemplatesFetch, onContactListsFetch]);

    const closeCreateWizard = useCallback(() => {
        setIsCreateWizardOpen(false);
    }, []);

    const handleCampaignCreated = useCallback(async (campaign: Campaign) => {
        await invalidateCampaigns();
        const cached = qc.getQueryData<{ items?: Campaign[] }>(
            queryKeys.outreach.campaigns.list(filters)
        );
        const items = cached?.items || [];
        const newId = campaign?.id || (items[0]?.id ?? null);
        if (newId) {
            setSelectedId(newId);
        }
    }, [invalidateCampaigns, qc, filters]);

    // =========================================================================
    // FORM HANDLERS
    // =========================================================================
    const handleCampaignFieldChange = useCallback(<K extends keyof CampaignFormData>(
        field: K,
        value: CampaignFormData[K]
    ) => {
        setCampaignForm(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleUpdateCampaign = useCallback(async () => {
        if (!selectedId) return;
        const trimmedName = campaignForm.name.trim();
        if (!trimmedName) {
            setError('Название не может быть пустым');
            return;
        }
        setCampaignSaving(true);
        setError(null);
        try {
            const payload = {
                name: trimmedName,
                description: campaignForm.description?.trim() || null,
                subjectTemplate: campaignForm.subjectTemplate?.trim() || null,
                bodyTemplate: campaignForm.bodyTemplate?.trim() || null,
                fromAccountId: campaignForm.fromAccountId || null,
                templateId: campaignForm.templateId || null,
                // Phase 97.8 W2: instantly* fields dropped from update payload.
                minDelay: Number(campaignForm.minDelay) || 60,
                maxDelay: Number(campaignForm.maxDelay) || 120,
                dailyLimit: Number(campaignForm.dailyLimit) || 0
            };
            await apiUpdateCampaign(selectedId, payload);
            await Promise.all([invalidateCampaigns(), invalidateDetail()]);
        } catch (err) {
            console.error('Failed to update campaign', err);
            const apiError = err as { data?: { error?: string }; message?: string };
            setError(apiError?.data?.error || apiError.message || 'Unknown error');
        } finally {
            setCampaignSaving(false);
        }
    }, [campaignForm, selectedId, invalidateCampaigns, invalidateDetail]);

    // =========================================================================
    // CRUD OPERATIONS
    // =========================================================================
    const handleDuplicateCampaign = useCallback(async (campaignId?: string) => {
        const targetId = campaignId || selectedId;
        if (!targetId || actionInProgress) return;

        setActionInProgress('duplicate');
        setError(null);
        try {
            await apiDuplicateCampaign(targetId);
            await invalidateCampaigns();
            setSelectedId(null);
            setNotice('Кампания дублирована (с контактами)');
        } catch (err) {
            console.error('Failed to duplicate campaign', err);
            const apiError = err as { data?: { error?: string }; message?: string };
            setError(apiError?.data?.error || apiError.message || 'Unknown error');
        } finally {
            setActionInProgress(null);
        }
    }, [selectedId, invalidateCampaigns, actionInProgress]);

    // =========================================================================
    // RECIPIENTS MANAGEMENT
    // =========================================================================
    interface ParsedRecipient { email: string; name?: string; }

    const parseRecipientInput = useCallback((raw: string): ParsedRecipient[] => {
        if (!raw || typeof raw !== 'string') return [];
        const lines = raw.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
        const items: ParsedRecipient[] = [];

        for (const line of lines) {
            let email = '';
            let name = '';

            const angleMatch = line.match(/^(.*)<([^<>]+@[^<>]+)>$/);
            if (angleMatch) {
                name = angleMatch[1].trim().replace(/^["']|["']$/g, '');
                email = angleMatch[2].trim();
            } else if (line.includes(',')) {
                const [first, second] = line.split(',').map(part => part.trim());
                if (second && second.includes('@')) {
                    name = first;
                    email = second;
                } else if (first && first.includes('@')) {
                    email = first;
                    name = second;
                }
            } else if (line.includes('@')) {
                const match = line.match(/([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/i);
                if (match) {
                    email = match[1];
                    name = line.replace(match[0], '').replace(/[<>]/g, '').replace(/["']/g, '').trim();
                }
            }

            if (email) {
                items.push({ email: email.toLowerCase(), name: name || undefined });
            }
        }
        return items;
    }, []);

    const handleAddRecipients = useCallback(async () => {
        if (!selectedId) return;
        const parsed = parseRecipientInput(recipientsInput);
        if (!parsed.length) {
            setError('Добавьте хотя бы один контакт');
            return;
        }
        setAddingRecipients(true);
        setError(null);
        try {
            await apiAddRecipients(selectedId, parsed);
            setRecipientsInput('');
            await Promise.all([invalidateCampaigns(), invalidateDetail()]);
        } catch (err) {
            console.error('Failed to add recipients', err);
            const apiError = err as { data?: { error?: string }; message?: string };
            setError(apiError?.data?.error || apiError.message || 'Unknown error');
        } finally {
            setAddingRecipients(false);
        }
    }, [invalidateCampaigns, invalidateDetail, selectedId, parseRecipientInput, recipientsInput]);

    const handleRemoveRecipient = useCallback(async (recipientId: string) => {
        if (!selectedId || !recipientId) return;
        try {
            await apiRemoveRecipient(selectedId, recipientId);
            // Optimistic update on TQ cache
            qc.setQueryData(
                queryKeys.outreach.campaigns.detail(selectedId),
                (prev: any) => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        recipients: prev.recipients?.filter((item: any) => item.id !== recipientId) || []
                    };
                }
            );
            await invalidateCampaigns();
        } catch (err) {
            console.error('Failed to remove recipient', err);
            const apiError = err as { data?: { error?: string }; message?: string };
            setError(apiError?.data?.error || apiError.message || 'Unknown error');
        }
    }, [invalidateCampaigns, selectedId, qc]);

    // =========================================================================
    // CONTACT LIST ATTACHMENT
    // =========================================================================
    const handleAttachListToCampaign = useCallback(async () => {
        if (!selectedId || !attachListId) return;
        setAttachLoading(true);
        setError(null);
        try {
            await apiAttachContactList(selectedId, attachListId);
            setAttachListId('');
            await Promise.all([invalidateCampaigns(), invalidateDetail()]);
        } catch (err) {
            console.error('Failed to attach contact list', err);
            const apiError = err as { data?: { error?: string }; message?: string };
            setError(apiError?.data?.error || apiError.message || 'Unknown error');
        } finally {
            setAttachLoading(false);
        }
    }, [attachListId, selectedId, invalidateCampaigns, invalidateDetail]);

    const handleDetachListFromCampaign = useCallback(async (listId: string) => {
        if (!selectedId || !listId) return;
        setAttachLoading(true);
        setError(null);
        try {
            await apiDetachContactList(selectedId, listId);
            await Promise.all([invalidateCampaigns(), invalidateDetail()]);
        } catch (err) {
            console.error('Failed to detach contact list', err);
            const apiError = err as { data?: { error?: string }; message?: string };
            setError(apiError?.data?.error || apiError.message || 'Unknown error');
        } finally {
            setAttachLoading(false);
        }
    }, [selectedId, invalidateCampaigns, invalidateDetail]);

    // =========================================================================
    // DERIVED STATE
    // =========================================================================
    // Prefer memberships (actual enrollment data) over legacy recipients
    const memberships = details?.memberships || [];
    const legacyRecipients = details?.recipients || [];
    const recipients = memberships.length > 0 ? memberships : legacyRecipients;
    const selectedCampaignSummary = campaigns.find(item => item.id === selectedId) || null;

    // =========================================================================
    // RETURN
    // =========================================================================
    return {
        campaigns,
        loading,
        selectedId,
        setSelectedId,
        details,
        detailsLoading,
        error,
        notice,
        campaignForm,
        campaignSaving,
        recipients,
        recipientsInput,
        setRecipientsInput,
        addingRecipients,
        testEmail,
        setTestEmail,
        testStatus,
        attachListId,
        setAttachListId,
        attachLoading,
        actionInProgress,
        selectedCampaignSummary,
        ownerFilter,
        setOwnerFilter,
        userRole,
        teams,
        managers,
        teamFilter,
        setTeamFilter,
        managerFilter,
        setManagerFilter,
        isCreateWizardOpen,
        startCreateCampaign,
        closeCreateWizard,
        handleCampaignCreated,
        fetchCampaigns,
        fetchDetails,
        handleCampaignFieldChange,
        handleUpdateCampaign,
        handleDuplicateCampaign,
        handleAddRecipients,
        handleRemoveRecipient,
        handleAttachListToCampaign,
        handleDetachListFromCampaign
    };
};

export default useCampaignState;
